// src/shared/hooks/useChartExport.ts
//
// Library-agnostic chart -> image export hook. Rasterizes any DOM/SVG
// container (Recharts LineChart/ComposedChart/AreaChart, etc.) to a PNG or
// JPEG data URL and triggers a browser download.
//
// Design goals:
//  - DECOUPLED from chart internals: we capture the rendered DOM, so future
//    Recharts refactors never break export.
//  - LEAN main bundle: `html-to-image` is dynamically imported on first use
//    (code-split) and memoized thereafter.
//  - THEME-SAFE: if no backgroundColor is supplied we auto-detect the app's
//    dark/light theme (documentElement `.dark` class) and force a solid
//    background so exports never come out transparent.
//  - ROBUST: temporarily disables CSS animation for a stable capture,
//    filters out the transient Recharts tooltip, and always restores the
//    node's style even if capture throws.

import { useCallback } from 'react';

export interface ExportOpts {
    /** Filename without extension. */
    filename?: string;
    /** Device pixel ratio for crispness (1..3). 2 is a good default. */
    pixelRatio?: number;
    /** Force a solid background. If omitted, the current theme is auto-detected. */
    backgroundColor?: string;
    /** JPEG quality 0..1 (ignored for PNG). */
    quality?: number;
    /** Optional inline style overrides applied during capture. */
    style?: Partial<CSSStyleDeclaration>;
}

// Lazily-loaded singleton so the html-to-image chunk is fetched at most once.
type HtmlToImage = typeof import('html-to-image');
let libPromise: Promise<HtmlToImage> | null = null;

const loadLib = (): Promise<HtmlToImage> => {
    if (!libPromise) {
        libPromise = import('html-to-image');
    }
    return libPromise;
};

/** Resolve a theme-appropriate solid background when the caller didn't pin one. */
const resolveThemeBackground = (): string => {
    if (typeof document === 'undefined') return '#ffffff';
    const isDark = document.documentElement.classList.contains('dark');
    return isDark ? '#0f172a' /* slate-900 */ : '#ffffff';
};

export function useChartExport(containerRef: React.RefObject<HTMLElement | null>) {
    const toDataUrl = useCallback(
        async (format: 'png' | 'jpeg', opts: ExportOpts = {}): Promise<{ dataUrl: string; filename: string }> => {
            const node = containerRef.current;
            if (!node) throw new Error('Chart container not available');

            const {
                filename = 'chart',
                pixelRatio = 2,
                backgroundColor = resolveThemeBackground(),
                quality = 0.95,
                style,
            } = opts;

            const lib = await loadLib();

            // Temporarily disable CSS animations for a stable, non-blurred capture.
            const prevAnimation = node.style.animation;
            node.style.animation = 'none';

            try {
                const common = {
                    pixelRatio,
                    backgroundColor,
                    cacheBust: true,
                    style: {
                        transform: 'translateZ(0)',
                        ...style,
                    } as Partial<CSSStyleDeclaration>,
                    // Strip the floating Recharts tooltip from the capture.
                    filter: (n: HTMLElement) => !n.classList?.contains('recharts-tooltip-wrapper'),
                };

                const dataUrl =
                    format === 'png'
                        ? await lib.toPng(node, common)
                        : await lib.toJpeg(node, { ...common, quality });

                return { dataUrl, filename };
            } finally {
                node.style.animation = prevAnimation;
            }
        },
        [containerRef],
    );

    const download = useCallback((dataUrl: string, filename: string) => {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, []);

    const downloadPng = useCallback(
        async (opts?: ExportOpts) => {
            const { dataUrl, filename } = await toDataUrl('png', opts);
            download(dataUrl, `${filename}.png`);
        },
        [download, toDataUrl],
    );

    const downloadJpeg = useCallback(
        async (opts?: ExportOpts) => {
            const { dataUrl, filename } = await toDataUrl('jpeg', opts);
            download(dataUrl, `${filename}.jpg`);
        },
        [download, toDataUrl],
    );

    return { downloadPng, downloadJpeg };
}

export default useChartExport;
